import React, { useState } from 'react';
import {
  Calendar, Clock, Users, AlertTriangle, CheckCircle, 
  Plus, Search, Filter, Eye, Edit, Trash2, User,
  Building2, Phone, Mail, MapPin, CalendarDays, Clock3
} from 'lucide-react';

interface Appointment {
  id: string;
  patientName: string;
  type: 'consultation' | 'medication_review' | 'vaccination' | 'pickup' | 'follow_up';
  date: string;
  time: string;
  duration: number;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  pharmacist: string;
  notes?: string;
}

interface StaffSchedule {
  id: string;
  staffName: string;
  role: 'pharmacist' | 'technician' | 'intern' | 'manager';
  date: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'confirmed' | 'sick' | 'vacation';
  location: string;
}

interface PharmacyEvent {
  id: string;
  title: string;
  type: 'training' | 'meeting' | 'inventory' | 'maintenance' | 'holiday';
  date: string;
  startTime: string;
  endTime: string;
  attendees: string[];
  location: string;
  description: string;
}

const mockAppointments: Appointment[] = [
  {
    id: 'APT-001',
    patientName: 'John Smith',
    type: 'consultation',
    date: '2024-01-20',
    time: '09:00',
    duration: 30,
    status: 'confirmed',
    pharmacist: 'Dr. Sarah Johnson',
    notes: 'New patient consultation for diabetes management'
  },
  {
    id: 'APT-002',
    patientName: 'Emily Davis',
    type: 'medication_review',
    date: '2024-01-20',
    time: '10:30',
    duration: 45,
    status: 'scheduled',
    pharmacist: 'Dr. Michael Brown',
    notes: 'Annual medication review and refill authorization'
  },
  {
    id: 'APT-003',
    patientName: 'Robert Wilson',
    type: 'vaccination',
    date: '2024-01-20',
    time: '14:00',
    duration: 20,
    status: 'scheduled',
    pharmacist: 'Dr. Sarah Johnson',
    notes: 'Flu shot and COVID booster'
  },
  {
    id: 'APT-004',
    patientName: 'Lisa Anderson',
    type: 'pickup',
    date: '2024-01-20',
    time: '16:00',
    duration: 15,
    status: 'confirmed',
    pharmacist: 'Dr. Michael Brown',
    notes: 'Prescription pickup with consultation'
  }
];

const mockStaffSchedule: StaffSchedule[] = [
  {
    id: 'SCH-001',
    staffName: 'Dr. Sarah Johnson',
    role: 'pharmacist',
    date: '2024-01-20',
    startTime: '08:00',
    endTime: '17:00',
    status: 'confirmed',
    location: 'Main Pharmacy'
  },
  {
    id: 'SCH-002',
    staffName: 'Dr. Michael Brown',
    role: 'pharmacist',
    date: '2024-01-20',
    startTime: '09:00',
    endTime: '18:00',
    status: 'confirmed',
    location: 'Main Pharmacy'
  },
  {
    id: 'SCH-003',
    staffName: 'Jennifer Lee',
    role: 'technician',
    date: '2024-01-20',
    startTime: '08:00',
    endTime: '16:00',
    status: 'confirmed',
    location: 'Main Pharmacy'
  },
  {
    id: 'SCH-004',
    staffName: 'David Chen',
    role: 'intern',
    date: '2024-01-20',
    startTime: '10:00',
    endTime: '15:00',
    status: 'confirmed',
    location: 'Main Pharmacy'
  }
];

const mockPharmacyEvents: PharmacyEvent[] = [
  {
    id: 'EVT-001',
    title: 'Staff Training: New Medication Protocols',
    type: 'training',
    date: '2024-01-22',
    startTime: '14:00',
    endTime: '16:00',
    attendees: ['Dr. Sarah Johnson', 'Dr. Michael Brown', 'Jennifer Lee'],
    location: 'Conference Room A',
    description: 'Training session on new medication protocols and safety procedures'
  },
  {
    id: 'EVT-002',
    title: 'Monthly Staff Meeting',
    type: 'meeting',
    date: '2024-01-25',
    startTime: '09:00',
    endTime: '10:00',
    attendees: ['All Staff'],
    location: 'Conference Room B',
    description: 'Monthly staff meeting to discuss operations and improvements'
  },
  {
    id: 'EVT-003',
    title: 'Inventory Count',
    type: 'inventory',
    date: '2024-01-28',
    startTime: '18:00',
    endTime: '22:00',
    attendees: ['Dr. Sarah Johnson', 'Jennifer Lee'],
    location: 'Main Pharmacy',
    description: 'Monthly inventory count and reconciliation'
  }
];

const getAppointmentTypeColor = (type: string) => {
  switch (type) {
    case 'consultation': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'medication_review': return 'bg-green-100 text-green-800 border-green-200';
    case 'vaccination': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'pickup': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'follow_up': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getAppointmentStatusColor = (status: string) => {
  switch (status) {
    case 'scheduled': return 'border-blue-500 bg-blue-50 text-blue-700';
    case 'confirmed': return 'border-green-500 bg-green-50 text-green-700';
    case 'completed': return 'border-gray-500 bg-gray-50 text-gray-700';
    case 'cancelled': return 'border-red-500 bg-red-50 text-red-700';
    case 'no_show': return 'border-orange-500 bg-orange-50 text-orange-700';
    default: return 'border-gray-500 bg-gray-50 text-gray-700';
  }
};

const getStaffRoleColor = (role: string) => {
  switch (role) {
    case 'pharmacist': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'technician': return 'bg-green-100 text-green-800 border-green-200';
    case 'intern': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'manager': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getEventTypeColor = (type: string) => {
  switch (type) {
    case 'training': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'meeting': return 'bg-green-100 text-green-800 border-green-200';
    case 'inventory': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'maintenance': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'holiday': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export function CalendarPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('2024-01-20');
  const [typeFilter, setTypeFilter] = useState('all');

  const filteredAppointments = mockAppointments.filter(appointment => {
    const matchesSearch = appointment.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         appointment.pharmacist.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = appointment.date === dateFilter;
    const matchesType = typeFilter === 'all' || appointment.type === typeFilter;
    
    return matchesSearch && matchesDate && matchesType;
  });

  const todayStats = {
    totalAppointments: mockAppointments.length,
    confirmed: mockAppointments.filter(a => a.status === 'confirmed').length,
    scheduled: mockAppointments.filter(a => a.status === 'scheduled').length,
    staffOnDuty: mockStaffSchedule.filter(s => s.status === 'confirmed').length
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#4B0082] via-[#6B38FB] to-purple-600 rounded-3xl p-10 text-white shadow-2xl shadow-[#6B38FB]/25">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
              <Calendar className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-black mb-2">Calendar & Scheduling</h1>
              <p className="text-xl text-purple-100">Manage appointments, staff schedules, and pharmacy events</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Today's Appointments</p>
              <p className="text-2xl font-black">{todayStats.totalAppointments}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Confirmed</p>
              <p className="text-2xl font-black text-green-300">{todayStats.confirmed}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Staff on Duty</p>
              <p className="text-2xl font-black">{todayStats.staffOnDuty}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Upcoming Events</p>
              <p className="text-2xl font-black">{mockPharmacyEvents.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search appointments, patients, staff..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            />
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Types</option>
              <option value="consultation">Consultation</option>
              <option value="medication_review">Medication Review</option>
              <option value="vaccination">Vaccination</option>
              <option value="pickup">Pickup</option>
              <option value="follow_up">Follow Up</option>
            </select>
          </div>
          
          <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Appointment</span>
          </button>
        </div>
      </div>

      {/* Today's Schedule Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Appointments */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
            <h3 className="text-xl font-bold text-gray-900">Today's Appointments</h3>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {filteredAppointments.map((appointment) => (
                <div key={appointment.id} className="group p-4 rounded-xl border-2 border-gray-200 hover:border-[#6B38FB] hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#6B38FB]">{appointment.time}</p>
                        <p className="text-xs text-gray-500">{appointment.duration}min</p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getAppointmentTypeColor(appointment.type)}`}>
                            {appointment.type.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getAppointmentStatusColor(appointment.status)}`}>
                            {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                          </span>
                        </div>
                        <h4 className="font-semibold text-gray-900">{appointment.patientName}</h4>
                        <p className="text-sm text-gray-600">with {appointment.pharmacist}</p>
                        {appointment.notes && (
                          <p className="text-xs text-gray-500 mt-1">{appointment.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Staff Schedule */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
            <h3 className="text-xl font-bold text-gray-900">Staff Schedule</h3>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {mockStaffSchedule.map((schedule) => (
                <div key={schedule.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{schedule.staffName}</h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStaffRoleColor(schedule.role)}`}>
                      {schedule.role.charAt(0).toUpperCase() + schedule.role.slice(1)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="flex items-center">
                      <Clock className="w-3 h-3 mr-2" />
                      {schedule.startTime} - {schedule.endTime}
                    </p>
                    <p className="flex items-center">
                      <MapPin className="w-3 h-3 mr-2" />
                      {schedule.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
          <h3 className="text-xl font-bold text-gray-900">Upcoming Events</h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockPharmacyEvents.map((event) => (
              <div key={event.id} className="group p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 hover:border-[#6B38FB] hover:shadow-lg transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getEventTypeColor(event.type)}`}>
                    {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                  </span>
                  <Calendar className="w-5 h-5 text-[#6B38FB]" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{event.title}</h4>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <p className="flex items-center">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {event.date} at {event.startTime} - {event.endTime}
                  </p>
                  <p className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {event.location}
                  </p>
                  <p className="flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    {event.attendees.length} attendees
                  </p>
                </div>
                <p className="text-sm text-gray-700">{event.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Plus className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">New Appointment</h4>
              <p className="text-sm text-gray-600">Schedule patient visit</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Staff Schedule</h4>
              <p className="text-sm text-gray-600">Manage work shifts</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Events</h4>
              <p className="text-sm text-gray-600">Plan pharmacy events</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Clock3 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Availability</h4>
              <p className="text-sm text-gray-600">Check open slots</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
